import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { BakeoffOutput } from '../components/BakeoffOutput';
import { alignFields, isMalformedReply, outputToFields } from '../utils/bakeoffFields';
import './TestBenchPage.css';
import './BakeoffPage.css';

/**
 * Model Bake-off (plan: docs/social-engine-plan.md, Chunks 12 and 13). Settings screen.
 * Every model is judged on the same words: picking a prompt picks its fixed panel
 * (Related or Game, three words each). Pick 2-4 models; outputs come back side by
 * side under letters. Rate each output 1-5 with a note, pick a winner per word to
 * reveal names, cost and time, then "Use winner for this prompt" sets that
 * prompt's model. The Archive tab compares every model ever run on a prompt and
 * panel series.
 */

type PanelType = 'related' | 'game';
interface BakeoffPrompt { key: string; label: string; task: string; source: PanelType; currentTarget: string | null }
interface Limits { maxRunUsd: number; minModels: number; maxModels: number; panelSize: number; maxNoteChars: number }
interface RegistryModel { id?: string; displayName: string; provider: string; modelId: string }
interface PanelWord { id: string; word: string; missing?: boolean }
interface Panel { type: PanelType; series: string | null; seriesNumber: number; setAt: string | null; words: PanelWord[]; empty: boolean }
interface Candidate { id: string; word: string; definition: string }
interface Proposal { id: string; word: string; role: string; why: string }
interface Row {
  id: string; run_id: string; prompt_key: string; task_name: string; word_id: string; word: string;
  slot: string; status: 'pending' | 'running' | 'done' | 'error'; output: string | null; error: string | null;
  is_winner: boolean; revealed: boolean; rating: number | null; note: string | null;
  model_target?: string; model_name?: string; provider?: string; model_id?: string;
  cost_usd?: number | null; duration_ms?: number | null; applied_at?: string | null;
}
interface Tally { model_target: string; model_name: string; wins: number }
interface RunSummary { run_id: string; prompt_key: string; panel_series: string | null; created_at: string; words: number; models: number; winners_picked: number; cost_usd: number; applied_at: string | null }
interface ArchiveNote { word: string; rating: number | null; note: string; created_at: string }
interface ArchiveModel {
  model_target: string; model_name: string; provider: string; runs: number; outputs: number; wins: number;
  avg_rating: number | null; rated: number; avg_cost_usd: number | null; avg_duration_ms: number | null;
  per_word: { word_id: string; word: string; avg_rating: number | null; rated: number }[];
  notes: ArchiveNote[];
}
interface ArchiveSeries { panel_series: string; runs: number; first_run: string; last_run: string; words: PanelWord[] }
interface Archive { series: string | null; seriesList: ArchiveSeries[]; words: PanelWord[]; models: ArchiveModel[] }

const PROVIDERS = ['anthropic', 'openai', 'openrouter', 'ollama'];
const PANEL_LABEL: Record<PanelType, string> = { related: 'Related panel', game: 'Game panel' };

function fmtUsd(n: number | null | undefined): string {
  if (n === null || n === undefined) return 'n/a';
  if (n === 0) return '$0 (local)';
  return n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(3)}`;
}
function fmtSecs(ms: number | null | undefined): string {
  return ms === null || ms === undefined ? 'n/a' : `${(ms / 1000).toFixed(1)}s`;
}
function fmtRating(n: number | null | undefined): string {
  return n === null || n === undefined ? '–' : n.toFixed(1);
}

export default function BakeoffPage() {
  const [tab, setTab] = useState<'run' | 'archive'>('run');
  const [prompts, setPrompts] = useState<BakeoffPrompt[]>([]);
  const [limits, setLimits] = useState<Limits | null>(null);
  const [models, setModels] = useState<RegistryModel[]>([]);
  const [promptKey, setPromptKey] = useState('');
  const [panels, setPanels] = useState<Record<PanelType, Panel> | null>(null);
  const [pickedModels, setPickedModels] = useState<string[]>([]);
  const [providerFilter, setProviderFilter] = useState('anthropic');
  const [showAllFields, setShowAllFields] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [tally, setTally] = useState<Tally[]>([]);
  const [estimate, setEstimate] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [history, setHistory] = useState<RunSummary[]>([]);

  // Panel editor
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PanelWord[]>([]);
  const [proposal, setProposal] = useState<Proposal[]>([]);
  const [candidateQuery, setCandidateQuery] = useState('');
  const [candidates, setCandidates] = useState<Candidate[]>([]);

  // Ratings being typed, per output
  const [drafts, setDrafts] = useState<Record<string, { rating: number | null; note: string }>>({});

  // Archive
  const [archive, setArchive] = useState<Archive | null>(null);
  const [archiveSeries, setArchiveSeries] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const prompt = prompts.find((p) => p.key === promptKey) || null;
  const panelType: PanelType | null = prompt?.source ?? null;
  const panel = panelType && panels ? panels[panelType] : null;
  const panelSize = limits?.panelSize ?? 3;

  const loadHistory = useCallback(async () => {
    try {
      const data = (await api.get('/api/bakeoff/runs')) as { runs: RunSummary[] };
      setHistory(data.runs || []);
    } catch { /* history is optional */ }
  }, []);

  const loadPanels = useCallback(async () => {
    const data = (await api.get('/api/bakeoff/panels')) as { panels: Record<PanelType, Panel> };
    setPanels(data.panels);
    return data.panels;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const p = (await api.get('/api/bakeoff/prompts')) as { prompts: BakeoffPrompt[]; limits: Limits };
        setPrompts(p.prompts);
        setLimits(p.limits);
        if (p.prompts.length) setPromptKey(p.prompts[0].key);
        const m = (await api.get('/api/config/models')) as { models?: RegistryModel[] };
        setModels((m.models || []).filter((x) => x.id));
        await loadPanels();
      } catch (err) {
        setError((err as Error).message);
      }
      loadHistory();
    })();
  }, [loadHistory, loadPanels]);

  // An empty panel: fetch a proposal and open the editor with it, so Odin can
  // accept or swap before the first run.
  useEffect(() => {
    if (!panel || !panelType || !panel.empty) return;
    (async () => {
      try {
        const data = (await api.get(`/api/bakeoff/panels/${panelType}/proposal`)) as { proposal: Proposal[] };
        setProposal(data.proposal);
        setDraft(data.proposal.map((w) => ({ id: w.id, word: w.word })));
        setEditing(true);
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, [panel, panelType]);

  // Approved words for the editor.
  useEffect(() => {
    if (!editing || !panelType) return;
    const t = setTimeout(async () => {
      try {
        const data = (await api.get(`/api/bakeoff/panels/${panelType}/candidates?q=${encodeURIComponent(candidateQuery)}`)) as { words: Candidate[] };
        setCandidates(data.words || []);
      } catch (err) {
        setError((err as Error).message);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [editing, panelType, candidateQuery]);

  const loadArchive = useCallback(async (key: string, series: string) => {
    if (!key) return;
    try {
      const data = (await api.get(`/api/bakeoff/archive?prompt=${encodeURIComponent(key)}&series=${encodeURIComponent(series)}`)) as Archive;
      setArchive(data);
      setArchiveSeries(data.series || '');
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    if (tab === 'archive') loadArchive(promptKey, '');
  }, [tab, promptKey, loadArchive]);

  const changePrompt = (key: string) => {
    const nextSource = prompts.find((p) => p.key === key)?.source;
    if (nextSource !== panelType) { setEditing(false); setProposal([]); setCandidateQuery(''); }
    setPromptKey(key);
  };

  const toggleModel = (id: string) => {
    setPickedModels((cur) => cur.includes(id)
      ? cur.filter((x) => x !== id)
      : cur.length >= (limits?.maxModels ?? 4) ? cur : [...cur, id]);
  };
  const modelName = (id: string) => models.find((m) => m.id === id)?.displayName || id;

  // ---- Panel editor ----
  const startEdit = () => {
    if (!panel) return;
    setDraft(panel.words.filter((w) => !w.missing).map((w) => ({ id: w.id, word: w.word })));
    setProposal([]);
    setCandidateQuery('');
    setEditing(true);
  };
  const toggleDraft = (w: PanelWord) => {
    setDraft((cur) => cur.some((x) => x.id === w.id)
      ? cur.filter((x) => x.id !== w.id)
      : cur.length >= panelSize ? cur : [...cur, { id: w.id, word: w.word }]);
  };
  const savePanel = async () => {
    if (!panel || !panelType) return;
    if (!panel.empty && !window.confirm(`Saving new ${PANEL_LABEL[panelType].toLowerCase()} words starts a new archive series. Results from ${panel.series} stay in the Archive under that series and are not mixed with the new one. Continue?`)) return;
    setError(null);
    try {
      const res = (await api.put(`/api/bakeoff/panels/${panelType}`, { wordIds: draft.map((w) => w.id) })) as { changed: boolean; panel: Panel };
      await loadPanels();
      setEditing(false);
      setProposal([]);
      setNotice(res.changed ? `${PANEL_LABEL[panelType]} set. New series: ${res.panel.series}.` : 'Same three words as before; nothing changed.');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const panelReady = !!panel && !panel.empty && panel.words.every((w) => !w.missing) && !editing;
  const canRun = !!prompt && !busy && panelReady && pickedModels.length >= (limits?.minModels ?? 2);

  // ---- Run ----
  const run = async () => {
    if (!prompt) return;
    setBusy(true); setError(null); setNotice(null); setRows([]); setTally([]); setRunId(null); setEstimate(null); setDrafts({});
    try {
      const created = (await api.post('/api/bakeoff/runs', { promptKey: prompt.key, modelIds: pickedModels })) as { runId: string; estimateUsd: number; rows: Row[] };
      setRunId(created.runId);
      setEstimate(created.estimateUsd);
      setRows(created.rows);
      // One request per output, all at once; each fills in as it finishes.
      await Promise.all(created.rows.map(async (r) => {
        try {
          const res = (await api.post(`/api/bakeoff/results/${r.id}/execute`, {})) as { row: Row };
          setRows((cur) => cur.map((x) => (x.id === r.id ? res.row : x)));
        } catch (err) {
          setRows((cur) => cur.map((x) => (x.id === r.id ? { ...x, status: 'error', error: (err as Error).message } : x)));
        }
      }));
      loadHistory();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const openRun = async (id: string) => {
    setError(null); setNotice(null); setDrafts({});
    try {
      const data = (await api.get(`/api/bakeoff/runs/${id}`)) as { rows: Row[]; tally: Tally[] };
      setRunId(id); setRows(data.rows); setTally(data.tally); setEstimate(null);
      if (data.rows[0]) setPromptKey(data.rows[0].prompt_key);
      setTab('run');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const pick = async (row: Row) => {
    setError(null);
    try {
      const data = (await api.post(`/api/bakeoff/results/${row.id}/pick`, {})) as { rows: Row[]; tally: Tally[] };
      setRows(data.rows); setTally(data.tally);
      loadHistory();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const draftFor = (r: Row) => drafts[r.id] ?? { rating: r.rating, note: r.note || '' };
  const setDraftFor = (r: Row, patch: Partial<{ rating: number | null; note: string }>) => {
    setDrafts((cur) => ({ ...cur, [r.id]: { ...draftFor(r), ...patch } }));
  };
  const saveRating = async (r: Row) => {
    const d = draftFor(r);
    setError(null);
    try {
      const res = (await api.post(`/api/bakeoff/results/${r.id}/rate`, { rating: d.rating, note: d.note })) as { row: Row };
      setRows((cur) => cur.map((x) => (x.id === r.id ? res.row : x)));
      setDrafts((cur) => { const next = { ...cur }; delete next[r.id]; return next; });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const useWinner = async (modelTarget: string) => {
    if (!runId) return;
    setError(null);
    try {
      const res = (await api.post(`/api/bakeoff/runs/${runId}/apply`, { modelTarget })) as { task: string; previous: string | null; current: string };
      setNotice(`${prompt?.label || res.task} now uses ${modelName(res.current)} (was ${res.previous ? modelName(res.previous) : 'unset'}).`);
      const p = (await api.get('/api/bakeoff/prompts')) as { prompts: BakeoffPrompt[] };
      setPrompts(p.prompts);
      loadHistory();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const byWord = useMemo(() => {
    const map = new Map<string, Row[]>();
    for (const r of rows) map.set(r.word_id, [...(map.get(r.word_id) || []), r]);
    for (const list of map.values()) list.sort((a, b) => a.slot.localeCompare(b.slot));
    return [...map.entries()];
  }, [rows]);

  // Every output for a word renders the same field list in the same order.
  const fieldsById = useMemo(() => {
    const out = new Map<string, ReturnType<typeof outputToFields>>();
    for (const [, list] of byWord) {
      const done = list.filter((r) => r.status === 'done');
      const aligned = alignFields(done.map((r) => outputToFields(r.output)));
      done.forEach((r, i) => out.set(r.id, aligned[i]));
    }
    return out;
  }, [byWord]);

  const allPicked = byWord.length > 0 && byWord.every(([, list]) => list.some((r) => r.is_winner));
  const topWins = tally[0]?.wins ?? 0;
  const leaders = tally.filter((t) => t.wins === topWins);
  const visibleModels = models.filter((m) => m.provider === providerFilter);
  const proposalFor = (id: string) => proposal.find((p) => p.id === id);

  return (
    <div className="testbench-page">
      <div className="testbench-header">
        <h1 className="testbench-title">Model bake-off</h1>
        <p className="testbench-subtitle">
          Every model is judged on the same words. Picking a prompt picks its panel: Related Word prompts use the Related panel,
          Game Word prompts use the Game panel. Pick {limits?.minModels ?? 2} to {limits?.maxModels ?? 4} models. Outputs come back
          under letters so you judge blind; rate each one, then pick a winner for each word to see which model wrote what,
          what it cost and how long it took. Nothing here touches game or social content. Runs over ${limits?.maxRunUsd ?? 1} are refused.
          No web search in bake-offs, so every model gets the same input.
        </p>
      </div>

      <div className="bk-provider-tabs">
        <button className={`tb-btn-secondary ${tab === 'run' ? 'bk-tab-active' : ''}`} onClick={() => setTab('run')}>Run</button>
        <button className={`tb-btn-secondary ${tab === 'archive' ? 'bk-tab-active' : ''}`} onClick={() => setTab('archive')}>Archive</button>
      </div>

      {error && (
        <div className="testbench-error">
          <span>{error}</span>
          <button className="tb-link-btn" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {notice && <div className="bk-notice">{notice}</div>}

      <section className="tb-panel">
        <h2 className="tb-panel-title">Prompt</h2>
        <select className="tb-input" value={promptKey} onChange={(e) => changePrompt(e.target.value)} disabled={busy}>
          {prompts.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        {prompt && (
          <p className="tb-panel-hint">
            Uses the {PANEL_LABEL[prompt.source].toLowerCase()}. Current model: {prompt.currentTarget ? modelName(prompt.currentTarget) : 'unset'}.
          </p>
        )}
      </section>

      {tab === 'run' && (
        <>
          <div className="testbench-controls tb-controls-stack">
            <section className="tb-panel">
              <h2 className="tb-panel-title">
                {panelType ? PANEL_LABEL[panelType] : 'Panel'}
                {panel?.series && <span className="tb-count">series {panel.series}</span>}
              </h2>
              {panel && !editing && (
                <>
                  {panel.empty
                    ? <p className="tb-empty">No words yet.</p>
                    : (
                      <div className="tb-word-chips">
                        {panel.words.map((w) => (
                          <span key={w.id} className={`tb-word-chip ${w.missing ? 'bk-missing' : ''}`} title={w.missing ? 'No longer on the approved list' : ''}>
                            {w.word}{w.missing ? ' (no longer approved)' : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  <button className="tb-btn-secondary" onClick={startEdit} disabled={busy}>Change panel words</button>
                </>
              )}
              {panel && editing && (
                <div className="bk-panel-editor">
                  {panel.empty
                    ? <p className="tb-panel-hint">This panel is empty. Here are three suggestions: accept them, or swap any for another approved word.</p>
                    : <p className="bk-warn">Saving different words starts a new archive series. Results from {panel.series} stay under that series and are not mixed with the new one.</p>}
                  <div className="tb-word-chips">
                    {draft.map((w) => {
                      const p = proposalFor(w.id);
                      return (
                        <span key={w.id} className="tb-word-chip" title={p ? p.why : ''}>
                          {w.word}{p ? ` · ${p.role}` : ''}
                          <button className="tb-chip-remove" onClick={() => toggleDraft(w)} title={`Remove ${w.word}`}>&times;</button>
                        </span>
                      );
                    })}
                    <span className="tb-count">{draft.length}/{panelSize}</span>
                  </div>
                  <input className="tb-input" placeholder="Search approved words" value={candidateQuery} onChange={(e) => setCandidateQuery(e.target.value)} />
                  <div className="tb-model-list tb-word-list">
                    {candidates.length === 0 && <p className="tb-empty">No matching approved words.</p>}
                    {candidates.map((w) => {
                      const checked = draft.some((x) => x.id === w.id);
                      return (
                        <label key={w.id} className={`tb-model-item ${checked ? 'checked' : ''}`} title={w.definition}>
                          <input type="checkbox" checked={checked} onChange={() => toggleDraft(w)} />
                          <span className="tb-model-name">{w.word}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="bk-row-actions">
                    <button className="tb-btn-primary" onClick={savePanel} disabled={draft.length !== panelSize}>
                      {panel.empty ? 'Use these words' : 'Save and start new series'}
                    </button>
                    {!panel.empty && <button className="tb-btn-secondary" onClick={() => setEditing(false)}>Cancel</button>}
                  </div>
                </div>
              )}
            </section>

            <section className="tb-panel">
              <h2 className="tb-panel-title">Models <span className="tb-count">{pickedModels.length}/{limits?.maxModels ?? 4}</span></h2>
              {pickedModels.length > 0 && (
                <div className="tb-word-chips">
                  {pickedModels.map((id) => (
                    <span key={id} className="tb-word-chip">
                      {modelName(id)}
                      <button className="tb-chip-remove" onClick={() => toggleModel(id)} title={`Remove ${modelName(id)}`}>&times;</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="bk-provider-tabs">
                {PROVIDERS.map((p) => (
                  <button key={p} className={`tb-btn-secondary ${providerFilter === p ? 'bk-tab-active' : ''}`} onClick={() => setProviderFilter(p)}>{p}</button>
                ))}
              </div>
              {providerFilter === 'ollama' && (
                <p className="tb-panel-hint">Ollama runs on your Mac. The engine can only reach it if the Ollama endpoint in Settings points at it and the model name is set.</p>
              )}
              <div className="tb-model-list">
                {visibleModels.length === 0 && <p className="tb-empty">No {providerFilter} models registered.</p>}
                {visibleModels.map((m) => {
                  const checked = pickedModels.includes(m.id as string);
                  return (
                    <label key={m.id} className={`tb-model-item ${checked ? 'checked' : ''}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleModel(m.id as string)} />
                      <span className="tb-model-name">{m.displayName}</span>
                      <span className="tb-model-meta">{m.modelId || '(no model name set)'}</span>
                    </label>
                  );
                })}
              </div>
            </section>
          </div>

          <div className="tb-run-bar">
            <button className="tb-btn-primary" onClick={run} disabled={!canRun}>{busy ? 'Running…' : 'Run bake-off'}</button>
            <span className="tb-run-note">
              {panel && !panel.empty ? panel.words.length : 0} word(s) x {pickedModels.length} model(s) = {(panel && !panel.empty ? panel.words.length : 0) * pickedModels.length} outputs
              {estimate !== null && ` · worst-case estimate ${fmtUsd(estimate)}`}
              {!panelReady && ' · set the panel words first'}
            </span>
          </div>

          {byWord.length > 0 && (
            <section className="tb-results">
              <label className="bk-show-all">
                <input type="checkbox" checked={showAllFields} onChange={(e) => setShowAllFields(e.target.checked)} />
                Show all fields
              </label>
              {byWord.map(([wordId, list]) => {
                const revealed = list.some((r) => r.is_winner);
                return (
                  <div key={wordId} className="bk-word-block">
                    <h2 className="tb-panel-title">{list[0].word} {revealed ? '· revealed' : '· blind'}</h2>
                    <div className="bk-grid" style={{ gridTemplateColumns: `repeat(${list.length}, minmax(0, 1fr))` }}>
                      {list.map((r) => {
                        const d = draftFor(r);
                        const dirty = d.rating !== r.rating || d.note !== (r.note || '');
                        return (
                          <div key={r.id} className={`bk-card ${r.is_winner ? 'bk-winner' : ''}`}>
                            <div className="bk-card-head">
                              <span className="bk-slot">{r.slot}</span>
                              {r.revealed && <span className="bk-model">{r.model_name} <em>({r.provider})</em></span>}
                            </div>
                            {r.status === 'pending' || r.status === 'running'
                              ? <p className="tb-empty">Running…</p>
                              : r.status === 'error'
                                ? <p className="bk-error">{r.error}</p>
                                : <BakeoffOutput fields={fieldsById.get(r.id) ?? outputToFields(r.output)} showAll={showAllFields} malformed={isMalformedReply(r.output)} />}
                            {r.revealed && (
                              <p className="bk-meta">Cost {fmtUsd(r.cost_usd)} · Time {fmtSecs(r.duration_ms)}</p>
                            )}
                            {r.status === 'done' && (
                              <div className="bk-rating">
                                <div className="bk-stars" role="group" aria-label={`Rate ${r.slot}`}>
                                  {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                      key={n}
                                      className={`bk-star ${d.rating !== null && n <= d.rating ? 'on' : ''}`}
                                      onClick={() => setDraftFor(r, { rating: d.rating === n ? null : n })}
                                      title={`${n} of 5`}
                                    >{n}</button>
                                  ))}
                                </div>
                                <textarea
                                  className="tb-input bk-note"
                                  placeholder="Note (optional)"
                                  maxLength={limits?.maxNoteChars ?? 2000}
                                  value={d.note}
                                  onChange={(e) => setDraftFor(r, { note: e.target.value })}
                                />
                                <button className="tb-btn-secondary" onClick={() => saveRating(r)} disabled={!dirty}>
                                  {dirty ? 'Save rating' : r.rating || r.note ? 'Saved' : 'Save rating'}
                                </button>
                              </div>
                            )}
                            {r.status === 'done' && !r.is_winner && (
                              <button className="tb-btn-secondary" onClick={() => pick(r)}>Pick {r.slot} as winner</button>
                            )}
                            {r.is_winner && <p className="bk-win-label">Winner</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {tally.length > 0 && (
                <div className="bk-apply">
                  <p>
                    Wins: {tally.map((t) => `${t.model_name} ${t.wins}`).join(' · ')}
                    {!allPicked && ' · pick the remaining words to finish'}
                  </p>
                  {leaders.map((t) => (
                    <button key={t.model_target} className="tb-btn-primary" onClick={() => useWinner(t.model_target)}>
                      Use winner for this prompt{leaders.length > 1 ? `: ${t.model_name}` : ''}
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          <section className="tb-panel tb-panel-full">
            <h2 className="tb-panel-title">Past bake-offs</h2>
            {history.length === 0 && <p className="tb-empty">None yet.</p>}
            {history.map((h) => (
              <button key={h.run_id} className="bk-history-row" onClick={() => openRun(h.run_id)}>
                <span>{new Date(h.created_at).toLocaleString()}</span>
                <span>{prompts.find((p) => p.key === h.prompt_key)?.label || h.prompt_key}</span>
                <span>{h.panel_series || 'no panel'}</span>
                <span>{h.words} word(s) x {h.models} models</span>
                <span>{h.winners_picked}/{h.words} picked</span>
                <span>{fmtUsd(h.cost_usd)}</span>
                <span>{h.applied_at ? 'winner applied' : ''}</span>
              </button>
            ))}
          </section>
        </>
      )}

      {tab === 'archive' && (
        <section className="tb-panel tb-panel-full">
          <h2 className="tb-panel-title">Archive</h2>
          {archive && archive.seriesList.length > 0 && (
            <select className="tb-input" value={archiveSeries} onChange={(e) => loadArchive(promptKey, e.target.value)}>
              {archive.seriesList.map((s) => (
                <option key={s.panel_series} value={s.panel_series}>
                  {s.panel_series}: {s.words.map((w) => w.word).join(', ') || 'words unknown'} ({s.runs} run{s.runs === 1 ? '' : 's'})
                </option>
              ))}
            </select>
          )}
          <p className="tb-panel-hint">
            One row per model ever run on this prompt and panel series. Only words whose winner you picked count, so an unfinished run stays blind.
            Sorted by average rating. Click a row for its notes.
          </p>
          {archive && archive.models.length === 0 && <p className="tb-empty">No judged results for this prompt and series yet.</p>}
          {archive && archive.models.length > 0 && (
            <div className="bk-archive-wrap">
              <table className="bk-archive">
                <thead>
                  <tr>
                    <th>Model</th>
                    {archive.words.map((w) => <th key={w.id}>{w.word}</th>)}
                    <th>Avg rating</th>
                    <th>Wins</th>
                    <th>Avg cost</th>
                    <th>Avg time</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {archive.models.map((m) => (
                    <Fragment key={m.model_target}>
                      <tr className="bk-archive-row" onClick={() => setExpanded(expanded === m.model_target ? null : m.model_target)}>
                        <td>{m.model_name} <em>({m.provider})</em><br /><span className="bk-meta">{m.runs} run{m.runs === 1 ? '' : 's'}</span></td>
                        {m.per_word.map((w) => <td key={w.word_id}>{fmtRating(w.avg_rating)}</td>)}
                        <td><strong>{fmtRating(m.avg_rating)}</strong></td>
                        <td>{m.wins}</td>
                        <td>{fmtUsd(m.avg_cost_usd)}</td>
                        <td>{fmtSecs(m.avg_duration_ms)}</td>
                        <td title={m.notes.map((n) => `${n.word}: ${n.note}`).join('\n')}>{m.notes.length ? `${m.notes.length} ▾` : '–'}</td>
                      </tr>
                      {expanded === m.model_target && m.notes.length > 0 && (
                        <tr className="bk-archive-notes">
                          <td colSpan={archive.words.length + 6}>
                            {m.notes.map((n, i) => (
                              <p key={i}><strong>{n.word}</strong>{n.rating ? ` (${n.rating}/5)` : ''}: {n.note} <span className="bk-meta">{new Date(n.created_at).toLocaleDateString()}</span></p>
                            ))}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
